import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { InputText, TInputTextProps } from './InputText';

type TInputMoneyProps = {
  emptyValue?: string;
  formatter?: (value: string) => string;
  hasFractions?: boolean;
  postfix?: string;
  prefix?: string;
} & Omit<TInputTextProps, 'onFocus'>;

const decimalRegexp = /^\d{0,10}(\.\d{0,2})?$/;
const integerRegexp = /^\d+$/;

const transformValue = (value?: number | string) => {
  return `${value}` === '0' ? '' : `${value}`;
};

export const InputMoney = forwardRef<HTMLInputElement, TInputMoneyProps>(
  function InputMoney(props: TInputMoneyProps, ref) {
    const {
      emptyValue = '0',
      formatter = (value: string) =>
        Intl.NumberFormat('en-US', {}).format(parseFloat(value)),
      hasFractions = true,
      onChange,
      postfix = '',
      prefix = '',
      value,
      ...rest
    } = props;
    const [localValue, setLocalValue] = useState(
      formatter(transformValue(value)),
    );

    const handleChange = useCallback(
      (value: string, event?: ChangeEvent<HTMLInputElement>) => {
        // Save cursor position and count separators in the original values
        const cursorPos = event?.target?.selectionStart;
        const oldValue = formatter(localValue || '');

        // Count separators in old and new values (without cleaning)
        const separatorRegex = /[,\s.](?=\d{3})/g;
        const oldSeparatorsCount = oldValue.match(separatorRegex)?.length || 0;
        const newSeparatorsCount =
          formatter(value).match(separatorRegex)?.length || 0;
        const separatorDiff = newSeparatorsCount - oldSeparatorsCount;

        const cleanValue = value.replaceAll(/[,\s.](?=\d{3})/g, '');
        const finalValue =
          value === '' ? emptyValue : cleanValue.replace(/\.$/, '.0');
        onChange?.(finalValue, event as ChangeEvent<HTMLInputElement>);
        setLocalValue(value);

        // Adjust cursor position accounting for separators
        if ((postfix || prefix) && cursorPos && event?.target) {
          requestAnimationFrame(() => {
            const { target } = event;
            if (!target?.selectionStart) return;

            const formattedValue = formatter(cleanValue);
            const displayValue = prefix + formattedValue + postfix;

            // Adjust cursor position for the separator count difference
            const cursorInValue = cursorPos - prefix.length;
            const adjustedCursorInValue = Math.max(
              0,
              cursorInValue + separatorDiff,
            );

            // Final cursor position
            let adjustedCursorPos: number;
            if (cursorPos <= prefix.length) {
              adjustedCursorPos = prefix.length;
            } else {
              adjustedCursorPos = Math.min(
                prefix.length + adjustedCursorInValue,
                displayValue.length - postfix.length,
              );
            }

            target.setSelectionRange(adjustedCursorPos, adjustedCursorPos);
          });
        }
      },
      [onChange, prefix, postfix, emptyValue, formatter, localValue],
    );

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e?.target?.value;
        if (value === '0') {
          e?.target?.select();
        } else {
          // Position the cursor after the prefix and before the postfix
          const formattedValue = formatter(value);
          const displayValue = prefix + formattedValue + postfix;

          const startPos = prefix.length;
          const endPos = Math.min(
            prefix.length + formattedValue?.length,
            displayValue.length - postfix.length,
          );
          e?.target?.setSelectionRange(startPos, endPos);
        }
      },
      [prefix, postfix, formatter],
    );

    useEffect(() => {
      const extValue = transformValue(value);
      if (
        localValue !== extValue &&
        extValue.replace(/\.0$/, '.') !== localValue
      )
        setLocalValue(extValue);
    }, [value, localValue]);

    return (
      <InputText
        allowedPattern={hasFractions ? decimalRegexp : integerRegexp}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={prefix + formatter('0') + postfix}
        size="xl"
        value={prefix + formatter(localValue) + postfix}
        ref={ref}
        // Filter only input-compatible props
        {...Object.fromEntries(
          Object.entries(rest).filter(([key, value]) => {
            // Exclude textarea-specific props
            if (key === 'rows' || key === 'cols' || key === 'wrap')
              return false;

            // For event handlers, allow only those that work with input
            if (key.startsWith('on') && typeof value === 'function') {
              // Allow basic event handlers
              const allowedHandlers = [
                'onCopy',
                'onCut',
                'onPaste',
                'onCompositionStart',
                'onCompositionEnd',
                'onCompositionUpdate',
                'onSelect',
                'onBeforeInput',
                'onInput',
                'onFocus',
                'onBlur',
                'onClick',
                'onDoubleClick',
                'onMouseDown',
                'onMouseUp',
                'onMouseEnter',
                'onMouseLeave',
                'onMouseMove',
                'onKeyDown',
                'onKeyUp',
                'onKeyPress',
                'onWheel',
                'onDrag',
                'onDragStart',
                'onDragEnd',
                'onDragOver',
                'onDrop',
                'onTouchStart',
                'onTouchEnd',
                'onTouchMove',
                'onContextMenu',
              ];
              return allowedHandlers.includes(key);
            }

            return true;
          }),
        )}
      />
    );
  },
);
